/**
 * CMS-editable per-country section visibility (AHD-13430).
 *
 * The code registry (`COUNTRY_GATES` in `lib/country-gates.ts`) stays the
 * default. The CMS `section-visibility` global lets marketing flip any
 * `section:` entry per market without a deploy:
 *
 *   CMS "show" / "hide"  →  wins
 *   CMS unset / "inherit" / global missing / fetch failed  →  registry default
 *
 * Pure and client-safe: the fetch lives in `lib/cms-server.ts`, the context in
 * `components/layout/section-visibility-provider.tsx`.
 */

import type { Country } from "@/i18n/config"
import { COUNTRY_GATES, type GateId } from "@/lib/country-gates"

/** Every market, not `knownCountryCodes` (which drops flag-disabled ones). */
const MARKETS: readonly Country[] = ["ch", "nl", "de", "at"]

export type SectionGateId = Extract<GateId, `section:${string}`>

/** Only `section:` gates are editable; pages/features stay in code. */
export const SECTION_GATE_IDS = (Object.keys(COUNTRY_GATES) as GateId[]).filter(
  (id): id is SectionGateId => id.startsWith("section:")
)

/** `section:go/bloodtest.hero-cta2` → `go_bloodtest_hero_cta2`. Mirrors the CMS field name. */
export function sectionFieldName(id: SectionGateId): string {
  return id.slice("section:".length).replace(/[^A-Za-z0-9]+/g, "_")
}

export type SectionVisibilityOverrides = Partial<
  Record<SectionGateId, Partial<Record<Country, boolean>>>
>

/**
 * Turn the raw `/globals/section-visibility` JSON into overrides. Anything that
 * isn't exactly "show" or "hide" is ignored, so a malformed, partial or empty
 * payload degrades to the registry.
 */
export function parseSectionVisibility(
  raw: unknown
): SectionVisibilityOverrides {
  const out: SectionVisibilityOverrides = {}
  if (!raw || typeof raw !== "object") return out
  const doc = raw as Record<string, unknown>
  for (const id of SECTION_GATE_IDS) {
    const group = doc[sectionFieldName(id)]
    if (!group || typeof group !== "object") continue
    for (const country of MARKETS) {
      const v = (group as Record<string, unknown>)[country]
      if (v === "show" || v === "hide") {
        ;(out[id] ??= {})[country] = v === "show"
      }
    }
  }
  return out
}

/** CMS value if set, else the code registry default. */
export function resolveSectionVisibility(
  id: GateId,
  country: Country,
  overrides?: SectionVisibilityOverrides | null
): boolean {
  const override = (
    overrides as
      | Record<string, Partial<Record<Country, boolean>>>
      | null
      | undefined
  )?.[id]?.[country]
  if (typeof override === "boolean") return override
  return (COUNTRY_GATES[id].countries as readonly string[]).includes(country)
}
